import type { MappingRuntimeFieldType } from '@elastic/elasticsearch/lib/api/types';
import type { RequestOptions, SearchRequest } from 'searchkit';

import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

// We make use of the requestOptions hook `beforeSearch` to alter the
// search request body. By doing that we're able to filter the results
// on the existence of the `computed_first_upcoming_date_index` field.
// That will filter out "past" events that only have dates from before today.
export const requestOptions: RequestOptions = {
  hooks: {
    beforeSearch: (requests: SearchRequest[]) => new Promise((resolve) => {
      requests.forEach((request) => {
        // eslint-disable-next-line
        (request.body.query?.bool?.filter as Array<any>)?.push({
          exists: {
            field: 'computed_first_upcoming_date_index',
          },
        });
      });

      resolve(requests);
    }),
  },
  getBaseFilters: () => [
    {
      bool: {
        must: {
          term: {
            type: {
              value: 'event',
            },
          },
        },
      },
    },
  ],
};

export const sorting: SearchkitWrapperProps['searchSettings']['sorting'] = {
  default: {
    field: 'computed_first_upcoming_start_date',
    order: 'asc',
  },
};

interface RuntimeMapping {
  type: MappingRuntimeFieldType;
  script: {
    source: string;
  };
}

// Define a runtime field that will loop over the `event_date` array
// to find the first upcoming date in the future. Then we can use this field
// to sort on that value.
// For infinite events (event_date_infinite is true), we emit today's date so they will appear.
export const computedFirstUpcomingStartDateMapping: RuntimeMapping = {
  type: 'date',
  script: {
    source: `
    if (params._source.event_date != null && params._source.event_date.length > 0) {
      def msPerDay = 86400000;
      def now = new Date().getTime();
      // Time at 00:00 today (start of day).
      def todayMidnight = now - (now % msPerDay);

      // Check if this is an infinite event.
      if (params._source.event_date_infinite != null) {
        for (int i = 0; i < params._source.event_date_infinite.length; i++) {
          if (params._source.event_date_infinite[i] == true) {
            // Infinite event: find first upcoming date from recurring_date_fields.
            if (params._source.recurring_date_fields != null) {
              for (int j = 0; j < params._source.recurring_date_fields.length; j++) {
                // Convert to ms.
                def recurringDateMs = params._source.recurring_date_fields[j] * 1000L;
                if (recurringDateMs >= todayMidnight) {
                  emit(recurringDateMs);
                  return;
                }
              }
            }
            // Fallback to todayMidnight if no upcoming recurring date found.
            emit(todayMidnight);
            return;
          }
        }
      }

      def firstUpcomingDate = null;
      def hasAnUpcomingDate = false;

      for (int i = 0; i < params._source.event_date.length; i++) {
        ZonedDateTime date = ZonedDateTime.parse(params._source.event_date[i] + 'Z');

        if (new Date(date.toInstant().toEpochMilli()).getTime() >= todayMidnight) {
          if (!hasAnUpcomingDate) {
            firstUpcomingDate = new Date(date.toInstant().toEpochMilli()).getTime();
            hasAnUpcomingDate = true;
          } else if (new Date(date.toInstant().toEpochMilli()).getTime() < firstUpcomingDate) {
            firstUpcomingDate = new Date(date.toInstant().toEpochMilli()).getTime();
          }
        }
      }

      if (firstUpcomingDate !== null) {
        emit(firstUpcomingDate)
      } else {
        return;
      }
    }
  `,
  },
};

// This runtime field will grab the index of the first upcoming date from
// the `event_date` array. We need that to grab the corresponding end date
// from the `event_date_end_value`.
// For infinite events (event_date_infinite is true), we emit 0 (the first/only date entry).
export const computedFirstUpcomingDateIndexMapping: RuntimeMapping = {
  type: 'long',
  script: {
    source: `
    if (params._source.event_date != null && params._source.event_date.length > 0) {
      // Check if this is an infinite event.
      if (params._source.event_date_infinite != null) {
        for (int i = 0; i < params._source.event_date_infinite.length; i++) {
          if (params._source.event_date_infinite[i] == true) {
            // Infinite event: find index of first upcoming date from recurring_date_fields.
            def msPerDay = 86400000;
            def now = new Date().getTime();
            def todayMidnight = now - (now % msPerDay);
            if (params._source.recurring_date_fields != null) {
              for (int j = 0; j < params._source.recurring_date_fields.length; j++) {
                // Convert to ms.
                def recurringDateMs = params._source.recurring_date_fields[j] * 1000L;
                if (recurringDateMs >= todayMidnight) {
                  emit(j);
                  return;
                }
              }
            }
            // Fallback to 0 if no upcoming recurring date found.
            emit(0);
            return;
          }
        }
      }

      def firstUpcomingDate = null;
      def firstUpcomingDateIndex = null;
      def hasAnUpcomingDate = false;
      def msPerDay = 86400000;
      def now = new Date().getTime();
      // Time at 00:00 todayMidnight (start of day).
      def todayMidnight = now - (now % msPerDay);

      for (int i = 0; i < params._source.event_date.length; i++) {
        ZonedDateTime date = ZonedDateTime.parse(params._source.event_date[i] + 'Z');

        if (new Date(date.toInstant().toEpochMilli()).getTime() >= todayMidnight) {
          if (!hasAnUpcomingDate) {
            firstUpcomingDate = new Date(date.toInstant().toEpochMilli()).getTime();
            firstUpcomingDateIndex = i;
            hasAnUpcomingDate = true;
          } else if (new Date(date.toInstant().toEpochMilli()).getTime() < firstUpcomingDate) {
            firstUpcomingDate = new Date(date.toInstant().toEpochMilli()).getTime();
            firstUpcomingDateIndex = i;
          }
        }
      }

      if (firstUpcomingDateIndex !== null) {
        emit(firstUpcomingDateIndex)
      } else {
        return;
      }
    }
  `,
  },
};

export const runtimeMappings: SearchkitWrapperProps['searchSettings']['runtime_mappings'] = {
  computed_first_upcoming_start_date: computedFirstUpcomingStartDateMapping,
  computed_first_upcoming_date_index: computedFirstUpcomingDateIndexMapping,
};
